/** @module Auth0LoggerSumoLogic */
/* eslint-disable import/extensions */
import SumoLogger from 'sumo-logger';
import Auth0LoggerBase from './auth0-logger-base.mjs';
import Auth0UserSessionHelper from '../auth0-session-helper.mjs';

// BROWSER ONLY!
class Auth0LoggerSumoLogic extends Auth0LoggerBase {
  constructor(options = {}) {
    super(options);

    console.log(`Initializing SumoLogic logger => ${options.sumoEndpoint}`);
    if (!options.sumoEndpoint) {
      throw new Error('Cannot create sumo logger. The `sumoEndpoint` (collector URL) is required.');
    }
    if (!options.sourceName) {
      throw new Error('Cannot create sumo logger. The `sourceName` (instantiating module) is required.');
    }

    // Store tenant class for auto-injection into all log messages
    this._tenantClass = options.tenantClass ?? null;

    const sumoBaseOptions = {
      interval: 0,
      sessionKey: Auth0UserSessionHelper.userId || 'anonymous user',
      hostName: Auth0UserSessionHelper.userId || 'anonymous user',
      endpoint: options.sumoEndpoint,
      ...options,
    };

    this._sumoLogger = new SumoLogger(sumoBaseOptions);

    this._sumoMetricsLogger = new SumoLogger({
      ...sumoBaseOptions,
    });
  }

  /**
   * Set the tenant class after initialization
   * @param {string} tenantClass - Tenant class (e.g., 'shipstation', 'shipstation-partners')
   */
  setTenantClass = (tenantClass) => {
    this._tenantClass = tenantClass ?? null;
  };

  _formatMessage = (msg, logLevel) => {
    return logLevel
      ? `${logLevel}: ${this._logMsgToString(msg)}`
      : this._logMsgToString(msg);
  };

  async log(msg, logLevel = 'INFO') {
    try {
      await this._sumoLogger.log(this._enrichAndFormatMsg(msg, logLevel), this.urlAndSessionData);
    } catch (error) {
      console.warn('SumoLogic log send has failed!', error);
    }
  }

  async trace(msg) {
    await this.log(msg, 'TRACE');
  }

  async debug(msg) {
    await this.log(msg, 'DEBUG');
  }

  async info(msg) {
    await this.log(msg, 'INFO');
  }

  async warn(msg) {
    await this.log(msg, 'WARN');
  }

  async error(msg) {
    await this.log(msg, 'ERROR');
  }

  async logMetric(metric) {
    // must contain `path` (dot separated) and a `value` (any)
    try {
      if (!metric || !metric.path || !metric.value) {
        throw new Error('Auth0LoggerSumoLogic: malformed metric object. Object must contain both `path` and `value` properties.');
      }
      await this._sumoMetricsLogger.log(metric);
    } catch (error) {
      console.warn('SumoLogic logMetric send has failed!', error);
    }
  }

  get urlAndSessionData() {
    return {
      url: window.location,
      sessionKey: Auth0UserSessionHelper.userId || 'anonymous user',
    };
  }

  _enrichAndFormatMsg = (msg, logLevel = 'INFO') => {
    // Build base context with tenant_class if available
    const baseContext = {
      logLevel,
    };
    if (this._tenantClass) {
      baseContext.tenant_class = this._tenantClass;
    }

    const userContext = Auth0UserSessionHelper.isLoggedIn
      ? {
        ...baseContext,
        sellerId: Auth0UserSessionHelper.userSellerId,
        userId: Auth0UserSessionHelper.userId,
        username: Auth0UserSessionHelper.userUserName,
        partition: Auth0UserSessionHelper.userPartition,
        nativeSsoSeller: Auth0UserSessionHelper.nativeSsoSellerIdp || 'false',
      } : baseContext;

    const defaultEnrich = () => {
      return {
        data: {
          msg,
          ...userContext,
        },
      };
    };
    try {
      if (typeof msg === 'string') {
        return defaultEnrich();
      }

      return {
        data: {
          ...userContext,
          ...msg,
        },
      };
    } catch (error) {
      console.warn(`Auth0LoggerSumoLogic: failed to enrich message "${msg}"`, error);
      return defaultEnrich();
    }
  };
}

export default Auth0LoggerSumoLogic;
